import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { type ReactNode, useId } from 'react';

interface TabPanelProps {
    index: number;
    value: number;
    tabIdPrefix: string;
    children: ReactNode;
}

export interface TabType {
    tab: number;
    tabLabel: string;
}

function CustomTabPanel({ children, value, index, tabIdPrefix }: TabPanelProps) {
    const tabId = `${tabIdPrefix}-tab-${index}`;
    const panelId = `${tabIdPrefix}-tabpanel-${index}`;

    return (
        <div role="tabpanel" hidden={value !== index} id={panelId} aria-labelledby={tabId}>
            {value === index && <>{children}</>}
        </div>
    );
}

interface Props {
    tabs: string[];
    contents: ReactNode[];
    tabComponents?: ReactNode;
    currentTab: TabType;
    setCurrentTab: (tab: TabType) => void;
}

export default function CommonTab({
    tabs,
    contents,
    tabComponents,
    currentTab,
    setCurrentTab,
}: Props) {
    const theme = useTheme();
    const tabIdPrefix = useId();

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab({ tab: newValue, tabLabel: tabs[newValue] });
    };

    return (
        <Box
            sx={{
                borderRadius: '8px',
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: 'neutral.white',
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                height: 'fit-content',
                marginX: '32px',
            }}
        >
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Tabs
                    value={currentTab.tab}
                    onChange={handleChange}
                    aria-label="intake-only-form"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ flex: 1, minWidth: 0 }}
                >
                    {tabs?.map((tab, idx) => {
                        const tabId = `${tabIdPrefix}-tab-${idx}`;
                        const panelId = `${tabIdPrefix}-tabpanel-${idx}`;

                        return (
                            <Tab
                                key={tab}
                                label={tab}
                                id={tabId}
                                aria-controls={panelId}
                                sx={{ textTransform: 'none' }}
                            />
                        );
                    })}
                </Tabs>
                {tabComponents && <Box sx={{ flexShrink: 0 }}>{tabComponents}</Box>}
            </Box>
            {contents.map((c, idx) => (
                <CustomTabPanel
                    key={idx}
                    value={currentTab.tab}
                    index={idx}
                    tabIdPrefix={tabIdPrefix}
                >
                    {c}
                </CustomTabPanel>
            ))}
        </Box>
    );
}
